import { useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../stores";
import locale from "../../../constants/locale";
import { ChatChannel, UserType } from "../../../models";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { Modal } from "../../../components";
import { FaExclamationTriangle } from "react-icons/fa";

type Props = {
  ongoingCall: boolean;
};

const ConnectButton = ({ ongoingCall }: Props) => {
  const { t } = useTranslation();
  const { users } = useStore("users");
  const { selectedChat, loadMessages, connect, clearBotForwarding } =
    useStore("chats");
  const { logEvent } = useStore("audit");
  const { user } = useStore("auth");
  const { selectedBot } = useStore("bots");
  const [isLoading, setIsLoading] = useState(false);
  const [isClearingForwarding, setIsClearingForwarding] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);

  const isChatExpired = useMemo(() => {
    if (!selectedChat?.userLastMessage) {
      return false;
    }
    try {
      const lastUserMessageDate = DateTime.fromJSDate(
        selectedChat?.userLastMessage,
      );
      const now = DateTime.now();
      const diffInHours = now.diff(lastUserMessageDate).as("hours");
      const maxDiffInHours = 24;
      return diffInHours > maxDiffInHours;
    } catch (error) {
      console.log("Error in isDiffIsMoreThen24Hours", error);
    }
  }, [selectedChat?.userLastMessage]);

  const channelIsWhatsApp = [
    ChatChannel.TwilioWhatsApp,
    ChatChannel.MetaWhatsApp,
  ].includes(selectedChat?.channel!);

  const handleClick = useCallback(
    async (force?: boolean) => {
      if (!selectedChat) {
        return;
      }

      if (!force && isChatExpired && channelIsWhatsApp) {
        setConfirmationModal(true);
        return;
      }

      setIsLoading(true);
      await connect();
      logEvent("agent_connected", {
        id: user?.id,
        email: user?.email,
        botId: selectedBot?.id,
        chatId: selectedChat.id,
      });

      loadMessages(200);

      setIsLoading(false);
    },
    [
      selectedChat,
      isChatExpired,
      channelIsWhatsApp,
      connect,
      logEvent,
      user?.id,
      user?.email,
      selectedBot?.id,
      loadMessages,
    ],
  );

  const otherAgentConnected =
    !!selectedChat?.userConnected && selectedChat?.userConnected !== user?.id;

  const agentConnected = useMemo(
    () => users?.find((f) => f.id === selectedChat?.userConnected),
    [selectedChat?.userConnected, users],
  );

  if (!selectedChat || ongoingCall) {
    return null;
  }

  let title = "";

  if (otherAgentConnected) {
    title = agentConnected
      ? locale.agentConnected
      : locale.anAgentIsAlreadyConnected;
  }

  const isForwardSource =
    !!selectedChat?.forwardBot &&
    Number(selectedChat?.data?.forward_source_bot_id) === selectedBot?.id;

  if (isForwardSource) {
    return (
      <div
        className="px-4 py-3 has-rounded-corners is-flex is-align-items-center"
        style={{
          background: "#FEF3CD",
          borderLeft: "4px solid #F0AD4E",
          gap: "12px",
        }}
      >
        <FaExclamationTriangle
          size={20}
          color="#D4930D"
          style={{ flexShrink: 0 }}
        />
        <div style={{ flex: 1 }}>
          <h2
            className="has-text-weight-semibold mb-1"
            style={{ fontSize: "13px", color: "#664D03" }}
          >
            {t(locale.chatForwardedToBot, { botId: selectedChat?.forwardBot })}
          </h2>
          <span style={{ fontSize: "12px", color: "#997404" }}>
            {t(locale.chatForwardedToBotDescription)}
          </span>
        </div>
        <button
          className={clsx("button is-small is-warning is-rounded", {
            "is-loading": isClearingForwarding,
          })}
          style={{ flexShrink: 0 }}
          disabled={isClearingForwarding}
          onClick={async () => {
            setIsClearingForwarding(true);
            await clearBotForwarding();
            setIsClearingForwarding(false);
          }}
        >
          {t(locale.clearBotForwarding)}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div
        className="control is-align-items-center is-flex"
        style={{ height: "100%" }}
      >
        <button
          style={{
            background: "#55CD6C",
          }}
          className={clsx(
            "button is-small has-text-weight-bold is-success is-rounded",
          )}
          disabled={
            isLoading ||
            (otherAgentConnected && user?.type === UserType.Collaborator) ||
            selectedChat.requestStatus === "sent"
          }
          title={t(title, { name: agentConnected?.name })}
          onClick={() => handleClick()}
        >
          <span className="icon">
            <img src="/assets/call.svg" alt="" />
          </span>
          <span> {t(locale.connect)}</span>
        </button>
      </div>
      <Modal
        isOpen={confirmationModal}
        title={() => (
          <h2 className="title is-5">
            {t(locale.customerServiceWindowExpired)}
          </h2>
        )}
        onClose={() => setConfirmationModal(false)}
        primaryText={t(locale.connect)}
        primaryLoading={isLoading}
        secondaryText={t(locale.cancel)}
        onPrimary={() => {
          setConfirmationModal(false);
          handleClick(true);
        }}
        onSecondary={() => setConfirmationModal(false)}
      >
        <span
          className="is-6 subtitle"
          style={{
            fontSize: "12px",
          }}
        >
          {t(locale.customerServiceWindowExpiredDescription)}{" "}
          <a
            href="https://business.whatsapp.com/policy"
            rel="noreferrer"
            target="_blank"
          >
            {t(locale.learnMore)}
          </a>
        </span>
      </Modal>
    </div>
  );
};

export default observer(ConnectButton);
